import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import EpmTooltip from './EpmTooltip';
import { ShotLightbox, ShotThumb } from './Screenshots';
import { useEPMContext } from '../../../contexts/EPMContext';
import { useUserContext } from '../../../contexts/UserContext';
import { permission } from '../../../config/permission';
import { createEpmLearning, getEpmLearningById, updateEpmLearning } from '../../../utils/APIs';
import EpmTagPicker from '../EpmTagPicker';
import { Input, Textarea, Select } from '../../UI';
import useAutoSave, { AUTOSAVE_DELAY, notifyAutoSaved } from './useAutoSave';
import { formatTestCardId } from '../../../utils/converter';
import { fmtDay, structureOf, testId } from '../epmHelpers';
import { fmtP, fmtPct, fmtSigned, hypothesisText, planOf, resultOf, scheduleOf, significancePath } from './stepData';
import { EPM_LIMITS } from '../validation/epmRules';
import { verdictStatusSchema, rationaleFormatSchema, learningAssumptionFormatSchema, resultLinkFormatSchema, createValidator } from '../validation/epmSchemas';
import EpmCharCounter from '../EpmCharCounter';
import useEpmValidation from '../validation/useEpmValidation';

const VERDICTS = ['win', 'loss', 'inconclusive', 'invalid'];
const CONFIDENCE_OPTIONS = ['High', 'Medium', 'Low'];
const APPLIES_TO_OPTIONS = ['Page type', 'Component', 'Property'];
const CONFIDENCE_SELECT_OPTIONS = CONFIDENCE_OPTIONS.map((option) => ({ value: option, label: option }));
const APPLIES_TO_SELECT_OPTIONS = APPLIES_TO_OPTIONS.map((option) => ({ value: option, label: option }));

const TH = 'font-mono font-semibold tracking-[.08em] uppercase text-[#8992a8] py-2.5 px-3 border-b border-[#e2e6f1]';
const TD = 'py-3.5 px-3 border-b border-[#edf0f6] text-[#334064] align-top';
const CHIP = 'inline-block px-2.5 py-0.5 border border-[#edf0f6] rounded-full bg-[#edf0f6] text-[11.5px] text-[#4d5873]';

const lowerFirst = (text) => String(text || '').charAt(0).toLowerCase() + String(text || '').slice(1);
const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const idOf = (ref) => ref?._id || ref;

// A saved learning in the shape the Results step sends, so the two compare field for field
const toLearningPayload = (doc) => ({
    current_assumption: doc.current_assumption || '',
    assumption_made: doc.assumption_made || '',
    outcome: doc.outcome || '',
    next_tests: doc.next_tests || '',
    current_assumption_reason: doc.current_assumption_reason || '',
    links: doc.links || [],
    confidence: doc.confidence || 'Low',
    applies_to: doc.applies_to || 'Page type',
    tag_ids: (doc.tag_ids || []).map(idOf),
});
// Same fields in the same order, so an unchanged learning compares equal and isn't saved again
const sameLearning = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const pct2 = (rate) => `${(rate * 100).toFixed(2)}%`;
const num = (value) => Math.round(value).toLocaleString('en-GB');

const ValidityRow = ({ label, value }) => (
    <li className="grid grid-cols-[24px_1fr_auto] gap-3 py-3 text-[13.5px] border-b border-[#edf0f6] last:border-0 items-center">
        <span>✓</span>
        <span className="flex items-center gap-1.5">{label}</span>
        <span className="text-[13px] text-[#737c95]">{value}</span>
    </li>
);

// The 95% interval against zero and the MDE (storyboard 06): −4% to +10% unless the interval needs more room
const IntervalChart = ({ uplift, ci, mde }) => {
    const [low, high] = ci;
    const axisMin = Math.min(-4, Math.floor(low) - 2);
    const axisMax = Math.max(10, Math.ceil(high) + 2);
    const x = (value) => 40 + ((value - axisMin) / (axisMax - axisMin)) * 600;
    return (
        <svg viewBox="0 0 680 92" className="mt-7 w-full max-w-full block h-auto" role="img" aria-label={`Uplift ${fmtPct(uplift)}, interval ${fmtSigned(low)} to ${fmtSigned(high)}, against zero and the ${mde}% MDE`}>
            <line x1="40" y1="60" x2="640" y2="60" stroke="#dce1ee" strokeWidth="1"/>
            <line x1={x(0)} y1="18" x2={x(0)} y2="60" stroke="var(--primary)" strokeWidth="1.5"/>
            <text x={x(0)} y="12" fontFamily="ui-monospace,monospace" fontSize="12" fill="#334064" textAnchor="middle">0</text>
            <line x1={x(mde)} y1="18" x2={x(mde)} y2="60" stroke="#8992a8" strokeWidth="1" strokeDasharray="3 3"/>
            <text x={x(mde)} y="12" fontFamily="ui-monospace,monospace" fontSize="12" fill="#737c95" textAnchor="middle">MDE {mde.toFixed(1)}%</text>
            <rect x={x(low)} y="32" width={Math.max(4, x(high) - x(low))} height="12" rx="6" fill="#e2e6f1"/>
            <circle cx={x(uplift)} cy="38" r="6" fill="var(--primary)"/>
            <g fontFamily="ui-monospace,monospace" fontSize="12" fill="#737c95" textAnchor="middle">
                <text x={x(low)} y="78">{fmtSigned(low)}</text><text x={x(high)} y="78">{fmtSigned(high)}</text>
                <text x="40" y="78">{fmtSigned(axisMin, 0)}%</text><text x="640" y="78">{fmtSigned(axisMax, 0)}%</text>
            </g>
        </svg>
    );
};

// p over the run, read only at the planned end
const SignificanceChart = ({ path }) => {
    if (!path.length) return <p className="text-[#737c95]">No significance history recorded.</p>;
    const days = path.length;
    const x = (day) => 40 + ((day - 1) / Math.max(1, days - 1)) * 400;
    const y = (p) => 130 - (Math.min(p, 0.3) / 0.3) * 110;
    const points = path.map((p, i) => `${i ? 'L' : 'M'}${x(i + 1).toFixed(1)},${y(p).toFixed(1)}`).join(' ');
    const ticks = [1, Math.round(days / 4), Math.round(days / 2), Math.round((3 * days) / 4), days];
    return (
        <svg viewBox="0 0 486 168" className="w-full block h-auto max-w-full" role="img" aria-label={`p-value over ${days} days`}>
            <g stroke="#edf0f6" strokeWidth="1"><line x1="40" y1="20" x2="440" y2="20"/><line x1="40" y1="57" x2="440" y2="57"/><line x1="40" y1="94" x2="440" y2="94"/></g>
            <line x1="40" y1="112" x2="440" y2="112" stroke="var(--primary)" strokeWidth="1" strokeDasharray="5 3"/>
            <text x="444" y="115" fontFamily="ui-monospace,monospace" fontSize="8.5" fill="#334064">α .05</text>
            <line x1="40" y1="130" x2="440" y2="130" stroke="#8992a8" strokeWidth="1"/>
            <g fontFamily="ui-monospace,monospace" fontSize="8.5" fill="#737c95" textAnchor="end"><text x="35" y="23">.30</text><text x="35" y="60">.20</text><text x="35" y="97">.10</text><text x="35" y="133">0</text></g>
            <path d={points} fill="none" stroke="var(--primary)" strokeWidth="2"/>
            <circle cx={x(days)} cy={y(path[days - 1])} r="4" fill="var(--primary)"/>
            <g fontFamily="ui-monospace,monospace" fontSize="8.5" fill="#737c95" textAnchor="middle">
                {ticks.map((day) => <text key={day} x={x(day)} y="146">d{day}</text>)}
            </g>
        </svg>
    );
};

const describePath = (path) => {
    if (!path.length) return 'No significance history recorded';
    const last = path.length - 1;
    const finalSig = path[last] < 0.05;
    const earlyDip = path.findIndex((p, i) => i < last && p < 0.05);
    if (!finalSig) return earlyDip >= 0 ? `dipped below .05 on day ${earlyDip + 1} by noise · ended above` : 'stayed above .05 throughout';
    let from = last;
    while (from > 0 && path[from - 1] < 0.05) from -= 1;
    if (earlyDip >= 0 && earlyDip < from - 1) return `dipped below .05 on day ${earlyDip + 1} by noise · settled below from day ${from + 1}`;
    return `settled below .05 from day ${from + 1}`;
};

const ResultsStep = ({ testData, setTestData, learningFlushRef }) => {
    const navigate = useNavigate();
    const { createTest, updateTest } = useEPMContext();
    const { saveNow, saveLater } = useAutoSave(testData);
    const { fieldErrors, validateFieldValue } = useEpmValidation();
    const { userData, checkPermission } = useUserContext();
    const canCreateTest = checkPermission(permission.XCM_TESTS_CREATE);
    const canUpdateTest = checkPermission(permission.XCM_TESTS_UPDATE);
    const canCreateLearning = checkPermission(permission.XCM_LEARNINGS_CREATE);
    const canUpdateLearning = checkPermission(permission.XCM_LEARNINGS_UPDATE);
    const canEditLearning = canUpdateLearning || canCreateLearning;
    const token = userData?.token;
    const orgToken = userData?.selectedOrganization?.org_token;

    const structure = structureOf(testData);
    const result = useMemo(() => resultOf(testData), [testData]);
    const plan = useMemo(() => planOf(testData), [testData]);
    const schedule = useMemo(() => scheduleOf(testData), [testData]);
    const concluded = testData?.status === 'CONCLUDED';
    const settling = result.settling && !concluded;
    const settleDays = Number((result.settlingNote.match(/(\d+)\s*d/) || [])[1]) || 1;
    const mockups = structure.mockups;
    const hasShots = !!(mockups?.control?.src && mockups?.variant?.src);

    const [nextIdea, setNextIdea] = useState(result.next);
    const [isCreatingIdea, setIsCreatingIdea] = useState(false);
    // Local state for the complex interactions and Nextbar validation
    const [validityOpen, setValidityOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [significanceOpen, setSignificanceOpen] = useState(false);
    const [businessImpactOpen, setBusinessImpactOpen] = useState(false);
    const [shotsOpen, setShotsOpen] = useState(false);
    // The saved verdict (testData.verdict) wins over the computed default
    const savedVerdict = testData?.verdict || {};
    const [verdict, setVerdict] = useState(savedVerdict.status ? savedVerdict.status.toLowerCase() : result.verdict); // 'win', 'loss', 'inconclusive', 'invalid' or null while due
    const [rationale, setRationale] = useState(savedVerdict.reason || result.rationale);
    const [learning, setLearning] = useState(result.learning);
    const [learningDetailsOpen, setLearningDetailsOpen] = useState(false);
    const [learningConfidence, setLearningConfidence] = useState(() => {

        const clearOfZero = result.ci[0] > 0 || result.ci[1] < 0;
        return verdict === 'inconclusive' || !clearOfZero ? 'Low' : result.p < 0.01 ? 'High' : 'Medium';
    });
    const [appliesTo, setAppliesTo] = useState('Page type');
    // A new learning starts with the test's tags
    const [learningTagIds, setLearningTagIds] = useState(() => (testData?.tag_ids || []).map(idOf));
    const [learningReason, setLearningReason] = useState('');
    const [whatHappened, setWhatHappened] = useState('');
    // The test's learning (learning_ids[0]) when the step opened; null for a new one
    const [initialLearningId] = useState(() => idOf(testData?.learning_ids?.[0]) || null);
    const [savedLinks, setSavedLinks] = useState([]);
    const [learningLoaded, setLearningLoaded] = useState(!initialLearningId);
    // Learning auto-save state: id once it exists, the last saved payload, whether
    // someone edited it, whether this visit has added its version, and the save queue
    const learningSync = useRef({ id: initialLearningId, saved: null, edited: false, versioned: false, queue: Promise.resolve(true) });
    const [gateOpen, setGateOpen] = useState(true);

    const [resultLink, setResultLink] = useState(testData?.result_link || '');

    // Keep the verdict and learning on the test so the gate bar, "Conclude test", and "Save and exit" see them
    useEffect(() => {
        setTestData((prev) => ({
            ...prev,
            verdict: { status: verdict ? capitalise(verdict) : '', reason: rationale || '' },
            learning: { current_assumption: learning || '' },
        }));
    }, [verdict, rationale, learning, setTestData]);

    useEffect(() => {
        if (testData?.result_link && !resultLink) {
            setResultLink(testData.result_link);
        }
    }, [testData?.result_link]);

    // Fill the form from the test's saved learning, if it has one
    useEffect(() => {
        if (!initialLearningId || !token || !orgToken) return;
        let cancelled = false;
        getEpmLearningById(token, orgToken, initialLearningId)
            .then((res) => {
                const doc = res?.data || res;
                if (cancelled || !doc) return;
                const saved = toLearningPayload(doc);
                setLearning(saved.current_assumption);
                setNextIdea(saved.next_tests);
                setLearningReason(saved.current_assumption_reason);
                setLearningConfidence(saved.confidence);
                setAppliesTo(saved.applies_to);
                setLearningTagIds(saved.tag_ids);
                setSavedLinks(saved.links);
                learningSync.current.saved = saved;
                setLearningLoaded(true);
            })
            .catch((error) => {
                // Left unloaded, so auto-save can't overwrite the saved learning with defaults
                if (!cancelled) toast.error(error?.message || 'Could not load the learning');
            });
        return () => { cancelled = true; };
    }, [initialLearningId, token, orgToken]);

    // The verdict saves on click; the rationale once typing pauses
    const chooseVerdict = (option) => {
        if (!canUpdateTest) return;
        try {
            verdictStatusSchema.validateSync(option);
        } catch (err) {
            toast.error(err.message || 'Invalid verdict.');
            return;
        }
        setVerdict(option);
        saveNow({ verdict: { status: capitalise(option), reason: rationale || '' } }, 'verdict');
    };

    const validateRationaleFormat = useMemo(() => createValidator(rationaleFormatSchema), []);

    const changeRationale = (value) => {
        if (!canUpdateTest) return;
        validateFieldValue('rationale', value, validateRationaleFormat);
        setRationale(value);
        saveLater(
            'verdict',
            (test) => ({ verdict: test?.verdict }),
            (test) => validateRationaleFormat(test?.verdict?.reason ?? '')
        );
    };

    const validateResultLinkFormat = useMemo(() => createValidator(resultLinkFormatSchema), []);

    const changeResultLink = (value) => {
        if (!canUpdateTest) return;
        validateFieldValue('resultLink', value, validateResultLinkFormat);
        setResultLink(value);
        setTestData((prev) => ({ ...prev, result_link: value }));
        saveLater(
            'result_link',
            () => ({ result_link: value }),
            () => validateResultLinkFormat(value)
        );
    };

    const validateLearningFormat = useMemo(() => createValidator(learningAssumptionFormatSchema), []);

    const handleLearningChange = (value) => {
        if (!canEditLearning) return;
        validateFieldValue('learning', value, validateLearningFormat);
        editLearning(setLearning)(value);
    };

    const { uplift, ci, p } = result;
    const [low, high] = ci;
    const mde = Number(plan.mde) || 3;
    const metricName = lowerFirst(plan.primary_metric);
    const sequential = plan.stop_rule === 'sequential';
    // EPM proposes a verdict only when the interval is clear of zero
    // const proposed = low > 0 ? 'win' : high < 0 ? 'loss' : null;
    const path = useMemo(() => significancePath(testData, result), [testData, result]);
    const lane = testData?.ideation?.location?.funnel_step || 'Page';

    // Arms split the exposures evenly (a clean sample ratio)
    const controlVisits = Math.round(result.exposures / 2);
    const variantVisits = result.exposures - controlVisits;
    const [controlRate, variantRate] = result.rates;
    const confidence = ((1 - p) * 100).toFixed(1);

    // Monthly effect from the interval, in conversions rather than currency (no order value joined)
    const monthly = (plan.metric.dailyTraffic || 0) * 30 * controlRate;
    const impactLow = Math.round((monthly * low) / 100 / 10) * 10;
    const impactHigh = Math.round((monthly * high) / 100 / 10) * 10;

    const concludedDay = schedule.end_day + 3;

    // const whatHappened = settling ? '' : `${fmtPct(uplift)} relative, CI ${fmtSigned(low)} – ${fmtSigned(high)}, p ${p < 0.001 ? '' : '= '}${fmtP(p)}`;
    const hypothesis = hypothesisText(testData);

    const learningPayload = useMemo(() => ({
        current_assumption: learning.trim(),
        assumption_made: hypothesis === '—' ? '' : hypothesis,
        outcome: whatHappened,
        next_tests: nextIdea.trim(),
        current_assumption_reason: learningReason.trim(),
        // Not edited on this step; kept so an update doesn't clear saved links
        links: savedLinks,
        confidence: learningConfidence,
        applies_to: appliesTo,
        tag_ids: learningTagIds,
    }), [learning, hypothesis, whatHappened, nextIdea, learningReason, savedLinks, learningConfidence, appliesTo, learningTagIds]);

    const latest = useRef({});
    latest.current = { payload: learningPayload, loaded: learningLoaded, testData, token, orgToken, updateTest, setTestData };

    // Saves the learning if it changed: creates it (and links it at learning_ids[0]) the
    // first time, then updates it. Only the first save in a visit adds a version.
    // Saves run one at a time, so the create finishes before the next save updates it.
    // Resolves false if the save failed. `force` saves a learning nobody edited (concluding).
    const persistLearning = useCallback(({ force = false } = {}) => {
        if (!canUpdateTest && !canCreateLearning && !canUpdateLearning) return Promise.resolve(true);
        const sync = learningSync.current;
        const run = async () => {
            const { payload, loaded, testData: test, token: tk, orgToken: org, updateTest: update, setTestData: setTest } = latest.current;
            // Never before a saved learning has loaded, so defaults can't overwrite it
            if (!loaded || (!sync.edited && !force)) return true;
            if (sync.saved && sameLearning(payload, sync.saved)) return true;
            try {
                if (sync.id) {
                    await updateEpmLearning(tk, org, sync.id, { ...payload, version_reason: '', ...(sync.versioned && { skip_version: true }) });
                } else {
                    // Nothing to save until "what we now believe" is written
                    if (!payload.current_assumption) return true;
                    const res = await createEpmLearning(tk, org, { ...payload, version_reason: '' });
                    const created = res?.data || res;
                    if (!created?._id) return true;
                    sync.id = created._id;
                    const learningIds = [created._id, ...(test?.learning_ids || []).map(idOf)];
                    await update(testId(test), { learning_ids: learningIds });
                    setTest((prev) => ({ ...prev, learning_ids: learningIds }));
                }
                sync.versioned = true;
                sync.saved = payload;
                notifyAutoSaved('Learning saved');
                return true;
            } catch (error) {
                console.error('Failed to save learning:', error);
                toast.error(error?.message || "Couldn't save the learning");
                return false;
            }
        };
        sync.queue = sync.queue.then(run);
        return sync.queue;
    }, []);

    const saveLearningLater = useMemo(() => debounce(() => persistLearning(), AUTOSAVE_DELAY), [persistLearning]);
    const flushLearning = useCallback((options) => {
        saveLearningLater.clear();
        return persistLearning(options);
    }, [saveLearningLater, persistLearning]);

    // Text fields save once typing pauses; dropdowns and tags straight away.
    // The save runs after the edit renders, so it sends the new value.
    const learningEdit = useRef(null);
    const editLearning = (setter, mode = 'later') => (value) => {
        learningEdit.current = mode;
        learningSync.current.edited = true;
        setter(value);
    };
    useEffect(() => {
        const mode = learningEdit.current;
        learningEdit.current = null;
        if (mode === 'later') saveLearningLater();
        if (mode === 'now') flushLearning();
    }, [learningPayload, saveLearningLater, flushLearning]);

    // The workflow flushes on "Conclude test" / "Save and exit"; leaving the step flushes too
    useEffect(() => {
        if (learningFlushRef) learningFlushRef.current = flushLearning;
        return () => {
            if (learningFlushRef) learningFlushRef.current = null;
            flushLearning();
        };
    }, [learningFlushRef, flushLearning]);

    const gate = {
        validity: !settling,
        verdict: !!verdict && !!rationale.trim(),
        learning: !!learning.trim(),
    };

    // A learning's follow-up becomes a new idea directly (01 §8.6)
    const handleCreateIdea = async () => {
        setIsCreatingIdea(true);
        try {
            const created = await createTest({
                name: nextIdea.trim(),
                description: `Follow-up to ${formatTestCardId(testData)}: ${learning}`,
                status: 'IDEAS',
                tag_ids: testData?.tag_ids || [],
            });
            toast.success(`${formatTestCardId(created)} created in Ideas`);
            if (testId(created)) navigate(`/epm/test-workflow/${testId(created)}`);
        } catch (error) {
            toast.error(error?.message || 'Could not create the idea');
        } finally {
            setIsCreatingIdea(false);
        }
    };

    const intervalCaption = () => {
        if (high < 0) return `Wholly below zero: the variant lowered ${metricName}. Even the upper bound is a loss.`;
        if (low <= 0) return 'The interval crosses zero, so this is inconclusive whatever the point estimate says.';
        if (low < mde) return `Clear of zero. The lower bound sits below the ${mde.toFixed(1)}% MDE, so the true effect may be smaller than planned for.`;
        return `Clear of zero, and the whole interval sits above the ${mde.toFixed(1)}% MDE.`;
    };

    // const verdictChip = () => {
    //     if (concluded) return `Recorded ${fmtDay(concludedDay)}${proposed && result.verdict && proposed === result.verdict ? ' · as proposed by EPM' : ''}`;
    //     return proposed ? `Verdict due · ${capitalise(proposed)} proposed by EPM` : 'Verdict due · no proposal, the interval spans zero';
    // };

    return (
        <div className="epm-plan epm-build">
            <header className="epm-workflow-step__heading epm-plan__heading">
                <span>06</span>
                <div>
                    <h2>Results</h2>
                    <p>Read the result honestly, record a verdict, and write down what we now believe. Everything needed to conclude is editable in place.</p>
                </div>
            </header>

            <div className="flex flex-col gap-8">

                {/* 1. Validity */}

                {/* Result Link */}
                <section className="epm-plan__card mb-0 px-7 py-6">
                    <div className="flex items-center mb-4">
                        <div className="text-[16px] font-semibold flex-1">Result Link</div>
                        {resultLink && (
                            <a
                                href={resultLink.startsWith('http://') || resultLink.startsWith('https://') ? resultLink : `https://${resultLink}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[12px] underline text-[#18181b] hover:text-[var(--primary)] flex items-center gap-1 cursor-pointer"
                            >
                                Open ↗
                            </a>
                        )}
                    </div>
                    <div className="flex items-center justify-between text-[12px] text-[#737c95] mb-1.5">
                        <div>Link to report or dashboard <span className="text-[#8992a8] ml-1">· optional</span></div>
                        <EpmCharCounter current={resultLink} max={EPM_LIMITS.RESULT_LINK_MAX} />
                    </div>
                    <Input
                        id="epm-field-result-link"
                        type="url"
                        disabled={!canUpdateTest}
                        maxLength={EPM_LIMITS.RESULT_LINK_MAX}
                        error={fieldErrors.resultLink}
                        placeholder="https://..."
                        value={resultLink}
                        onChange={(e) => changeResultLink(e.target.value)}
                    />
                </section>

                {/* 7. Verdict */}
                <section className="epm-plan__card mb-0 px-7 py-6" id="epm-field-verdict">
                    <div className="flex items-center mb-4">
                        <div className="text-[16px] font-semibold flex-1">Verdict</div>

                        <span className="ml-1.5"><EpmTooltip content="XCM preselects a verdict only when validity is clean, significance is unambiguous and no guardrail is near its limit. Otherwise nothing is selected. Both the proposed and the recorded verdict are stored." /></span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                        {VERDICTS.map((opt) => (
                            <span
                                key={opt}
                                onClick={() => canUpdateTest && !settling && chooseVerdict(opt)}
                                className={`border border-[#dce1ee] rounded-md py-2.5 text-center text-[13px] ${settling || !canUpdateTest ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${verdict === opt ? 'bg-[var(--primary)] text-white font-semibold border-[var(--primary)]' : 'bg-white text-[#4d5873] hover:bg-[#f8f9fd]'}`}
                            >
                                {capitalise(opt)}
                            </span>
                        ))}
                    </div>

                    <div className="flex items-center justify-between text-[12px] text-[#737c95] mb-1.5 mt-5">
                        <div>Rationale <span className="text-[#8992a8] ml-1">· required, written by you</span></div>
                        <EpmCharCounter current={rationale} max={EPM_LIMITS.RATIONALE_MAX} />
                    </div>
                    <Textarea
                        id="epm-field-rationale"
                        disabled={!canUpdateTest}
                        maxLength={EPM_LIMITS.RATIONALE_MAX}
                        error={fieldErrors.rationale}
                        value={rationale}
                        rows={3}
                        placeholder="Why this verdict: validity, the interval, anything that qualifies it"
                        onChange={(e) => changeRationale(e.target.value)}
                    />
                </section>

                {/* 8-10. Learning */}
                <section className="epm-plan__card mb-0 px-7 py-6">
                    <div className="flex items-center mb-5">
                        <div className="text-[16px] font-semibold flex items-center gap-2 flex-1">
                            Learning
                            <EpmTooltip content="Saved to the Learnings library as you type, linked to this test as its origin. Each visit that changes it is kept as a revision." />
                        </div>
                        {checkPermission(permission.XCM_LEARNINGS_VIEW) && (
                            <button type="button" onClick={() => navigate('/epm/learnings')} className="text-[#4d5873] text-[12px] bg-transparent border-0 cursor-pointer hover:text-[#26335f]">Open library ↗</button>
                        )}
                    </div>

                    <div className="flex items-center justify-between text-[12px] text-[#737c95] mb-1.5">
                        <div>What we now believe <span className="text-[#8992a8] ml-1">· required</span></div>
                        <EpmCharCounter current={learning} max={EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX} />
                    </div>
                    <Textarea
                        id="epm-field-learning"
                        disabled={!canEditLearning}
                        maxLength={EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX}
                        error={fieldErrors.learning}
                        value={learning}
                        rows={3}
                        placeholder="What this result makes you believe about customers, stated so it could be wrong"
                        onChange={(e) => handleLearningChange(e.target.value)}
                    />

                    <div className="relative mt-6 pt-2">
                        <div className="flex items-center justify-between text-[12px] text-[#737c95] mb-1.5">
                            <div>What to test next</div>
                            <EpmCharCounter current={nextIdea} max={EPM_LIMITS.WHAT_TO_TEST_NEXT_MAX} />
                        </div>
                        <div className="flex items-center gap-3">
                            <Input
                                id="epm-field-next-idea"
                                type="text"
                                disabled={!canEditLearning}
                                maxLength={EPM_LIMITS.WHAT_TO_TEST_NEXT_MAX}
                                className="flex-1"
                                value={nextIdea}
                                onChange={(e) => editLearning(setNextIdea)(e.target.value)}
                            />
                            {canCreateTest && (
                                <button type="button" onClick={handleCreateIdea} disabled={!canEditLearning || !nextIdea.trim() || isCreatingIdea} className="px-[11px] py-[6px] border border-border rounded-md bg-white text-[12px] text-stride-body hover:bg-surface">Create idea ↗</button>
                            )}
                        </div>
                        <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-4 mt-5">
                            <div>
                                <div className="stride-form-label flex items-center">Confidence</div>
                                <Select
                                    value={learningConfidence}
                                    onChange={(e) => editLearning(setLearningConfidence, 'now')(e.target.value)}
                                    options={CONFIDENCE_SELECT_OPTIONS}
                                    selectClassName="text-[13px] py-[9px]"
                                    disabled={!canEditLearning}
                                />
                            </div>
                            <div>
                                <div className="stride-form-label flex items-center">Applies to</div>
                                <Select
                                    value={appliesTo}
                                    onChange={(e) => editLearning(setAppliesTo, 'now')(e.target.value)}
                                    options={APPLIES_TO_SELECT_OPTIONS}
                                    selectClassName="text-[13px] py-[9px]"
                                    disabled={!canEditLearning}
                                />
                            </div>
                            <div>
                                <div className="stride-form-label flex items-center">Tags</div>
                                <EpmTagPicker selectedTagIds={learningTagIds} onChange={editLearning(setLearningTagIds, 'now')} id="results-learning-tag-picker" />
                            </div>
                        </div>
                    </div>

                    <div className="relative mt-6 border-t border-border-subtle">
                        <div className="epm-plan__collapsible flat m-0 p-0 border-0 bg-transparent">
                            <div className={`epm-plan__collapsible-header ${learningDetailsOpen ? 'is-open' : ''} pt-[10px] pb-[10px] px-0`} onClick={() => setLearningDetailsOpen(!learningDetailsOpen)}>
                                <ChevronDown size={15} />
                                <span className="epm-plan__collapsible-title text-[13px]">Result, hypothesis & reasoning</span>
                                <span className="epm-plan__collapsible-subtitle text-[13px]">
                                    prefilled · {settling || !Number.isFinite(uplift) ? 'result pending' : `${fmtPct(uplift)}, CI ${fmtSigned(low)} – ${fmtSigned(high)}`} · hypothesis carried forward · why: {learningReason.trim() ? 'written' : 'empty'}
                                </span>
                            </div>
                            {learningDetailsOpen && (
                                <div className="epm-plan__collapsible-body pl-[21px] pr-0 pb-2 pt-1">
                                    <div className="mb-5">
                                        <div className="flex items-center justify-between stride-form-label">
                                            <div>What happened <span className="text-stride-muted-light ml-1 font-normal">· from the result</span></div>
                                            <EpmCharCounter current={whatHappened} max={EPM_LIMITS.WHAT_HAPPENED_MAX} />
                                        </div>
                                        <Input
                                            id="epm-field-what-happened"
                                            type="text"
                                            disabled={!canEditLearning}
                                            maxLength={EPM_LIMITS.WHAT_HAPPENED_MAX}
                                            placeholder="Describe what happened..."
                                            value={whatHappened}
                                            onChange={(e) => editLearning(setWhatHappened)(e.target.value)}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <div className="stride-form-label flex items-center">What we believed <span className="text-stride-muted-light ml-1 font-normal">· carried forward from the hypothesis</span></div>
                                        <div className="border border-border rounded-md bg-white px-3 py-[9px] text-[13px] text-stride-heading">{hypothesis}</div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between stride-form-label">
                                            <div>Why we think that</div>
                                            <EpmCharCounter current={learningReason} max={EPM_LIMITS.WHY_WE_THINK_MAX} />
                                        </div>
                                        <Textarea
                                            id="epm-field-learning-reason"
                                            disabled={!canEditLearning}
                                            maxLength={EPM_LIMITS.WHY_WE_THINK_MAX}
                                            className="w-full"
                                            rows={3}
                                            value={learningReason}
                                            onChange={(e) => editLearning(setLearningReason)(e.target.value)}
                                            placeholder="Interpretation, caveats, alternative explanations..."
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>

            {/* 11. Nextbar */}
            {/* <div className="flex items-center gap-4 mt-12 pt-6 border-t border-[#e2e6f1] relative">
                <div className="epm-plan__collapsible flat flex-1 m-0 p-0 border-0 bg-transparent">
                    <div className={`epm-plan__collapsible-header ${gateOpen ? 'is-open' : ''} py-2.5 px-0`} onClick={() => setGateOpen(!gateOpen)}>
                        <ChevronDown size={15} />
                        <span className="epm-plan__collapsible-title text-[13px]">{concluded ? `Concluded ${fmtDay(concludedDay)}` : 'Ready to conclude'}</span>
                        <span className="epm-plan__collapsible-subtitle text-[13px]">
                            Validity {gate.validity ? '✓' : '✕'} · verdict {gate.verdict ? '✓' : '✕'} · learning {gate.learning ? '✓' : '✕'}
                        </span>
                    </div>
                    {gateOpen && (
                        <div className="epm-plan__collapsible-body pl-[21px] pr-0 pb-2 pt-1">
                            <ul className="m-0 p-0 list-none text-[13px] text-[#4d5873]">
                                <li className="py-1">{gate.validity ? '✓ Validity settled' : `✕ Validity settling — numbers unlock ${fmtDay(settleDays)}`}</li>
                                <li className="py-1">{gate.verdict ? '✓ Verdict chosen, rationale written' : `✕ ${verdict ? 'Rationale' : 'Verdict'} still to write`}</li>
                                <li className="py-1">{gate.learning ? '✓ Learning: "what we now believe" filled' : '✕ Learning: "what we now believe" is empty'}</li>
                            </ul>
                            {concluded && <p className="text-[12px] text-[#737c95] mt-2 mb-0">Saved to the Learnings library as {formatTestCardId(testData)}'s origin learning.</p>}
                        </div>
                    )}
                </div>
            </div> */}

            {shotsOpen && hasShots && <ShotLightbox mockups={mockups} onClose={() => setShotsOpen(false)} />}
        </div>
    );
};

export default ResultsStep;
